import { useEffect, useRef, useState } from "react"
import { detectSitePlatform } from "@/lib/api/detectPlatform"
import type { DetectSitePlatformResult, PlatformId } from "@/lib/types/platform"
import { inferPlatformFromDomain } from "@/lib/utils/detectPlatform"

type UseDetectSitePlatformArgs = {
  domain: string
  token: string
  propertyId?: string
  enabled?: boolean
}

type UseDetectSitePlatformResult = {
  platform: PlatformId
  detection: DetectSitePlatformResult | null
  isDetecting: boolean
  /** User overrode the auto-detected platform */
  isManual: boolean
  setPlatform: (platform: PlatformId) => void
  /** Merge a non-manual detection signal (e.g. from validate-setup). */
  applyDetection: (next: DetectSitePlatformResult) => void
  clearManualOverride: () => void
}

function isAbortError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    error.name === "AbortError"
  )
}

/**
 * Detects site platform for install guidance.
 * Starts with instant domain heuristics, then upgrades via Firecrawl when available.
 */
export function useDetectSitePlatform({
  domain,
  token,
  propertyId,
  enabled = true,
}: UseDetectSitePlatformArgs): UseDetectSitePlatformResult {
  const inferred = inferPlatformFromDomain(domain)
  const [detection, setDetection] = useState<DetectSitePlatformResult | null>(() =>
    domain
      ? {
          platform: inferred.platform,
          confidence: inferred.confidence,
          source: inferred.platform === "generic" ? "fallback" : "domain",
          signals: inferred.signals,
        }
      : null,
  )
  const [isDetecting, setIsDetecting] = useState(false)
  const [manualPlatform, setManualPlatform] = useState<PlatformId | null>(null)
  const requestIdRef = useRef(0)

  useEffect(() => {
    setManualPlatform(null)
  }, [domain])

  useEffect(() => {
    // Nothing to detect. A previous run's cleanup already invalidated its
    // in-flight request, so that request's .finally will skip clearing the
    // flag — clear it here or the UI stays stuck on "Detecting…".
    if (!enabled || !domain) {
      setIsDetecting(false)
      return
    }

    const requestId = ++requestIdRef.current
    const controller = new AbortController()

    const seed = inferPlatformFromDomain(domain)
    setDetection({
      platform: seed.platform,
      confidence: seed.confidence,
      source: seed.platform === "generic" ? "fallback" : "domain",
      signals: seed.signals,
    })
    setIsDetecting(true)

    // Token optional: without it we still HTML-scrape custom domains.
    detectSitePlatform({
      domain,
      token: token || "",
      propertyId,
      signal: controller.signal,
    })
      .then((result) => {
        if (requestId !== requestIdRef.current) return
        setDetection(result)
      })
      .catch((error: unknown) => {
        if (isAbortError(error)) return
        // Detection is best-effort; keep the seeded result if a fallback request fails unexpectedly.
      })
      .finally(() => {
        if (requestId !== requestIdRef.current) return
        setIsDetecting(false)
      })

    return () => {
      // detectSitePlatform resolves even when aborted, so invalidate this
      // request or its late fallback would overwrite the detection for a
      // domain the user has already moved away from.
      if (requestId === requestIdRef.current) {
        requestIdRef.current += 1
      }
      controller.abort()
    }
  }, [domain, token, propertyId, enabled])

  const platform = manualPlatform ?? detection?.platform ?? "generic"

  return {
    platform,
    detection,
    isDetecting,
    isManual: manualPlatform !== null,
    setPlatform: setManualPlatform,
    applyDetection: (next) => {
      if (manualPlatform !== null) return
      setDetection((prev) => {
        if (!prev) return next
        // Keep stronger existing signal unless the new one is at least as confident.
        if (prev.confidence > next.confidence) return prev
        return next
      })
    },
    clearManualOverride: () => setManualPlatform(null),
  }
}
